import React from 'react';
import PropTypes from 'prop-types';

import { IconEdit, Tag, Text } from '@zillow/constellation';

import StaticMap from '../StaticMap';

import {
  StyledAddress,
  StyledFeedTag,
  StyledFlex,
  StyledInlineSpacer,
  StyledOverlay,
  StyledPhotoContainer,
  StyledPhotoGrid,
  StyledPhotoOverlay,
} from './styles';
import type { Listing } from '../declarations/Listing';

const DEFAULT_GOOGLE_STATIC_MAP = {
  LAT: 47.6072514,
  LON: -122.3387425,
};

const propTypes = {
  onClickEdit: PropTypes.func,
  signMapUrl: PropTypes.func,
  listing: PropTypes.object,
};

export interface PhotoGridPDPProps {
  listing: Listing;
  onClickEdit?: () => void;
  signMapUrl?: (url: string, brand: string) => Promise<string>;
}

interface Photo {
  url: string;
}

const PhotoGridPDP = ({ listing, onClickEdit, signMapUrl, ...props }: PhotoGridPDPProps) => {
  const { active, isFeed, city, geo, isCommunity, isWaitlisted, propertyPhotos, state, street, unit, zip, name } =
    listing;
  const lat = geo ? geo.lat : DEFAULT_GOOGLE_STATIC_MAP.LAT;
  const lng = geo ? geo.lon : DEFAULT_GOOGLE_STATIC_MAP.LON;
  const hasMap = lat && lng && signMapUrl;
  const maxImages = hasMap ? 4 : 5;
  const imageArr = propertyPhotos.map((photo: Photo) => photo.url).slice(0, maxImages);
  const gridSize = hasMap ? imageArr.length + 1 : imageArr.length;

  const getMapSize = (len: number) => {
    let height;
    let width;

    if (len >= 3) {
      width = 256;
      height = 109;
    } else if (len === 2) {
      width = 256;
      height = 220;
    } else if (len === 1) {
      width = 514;
      height = 220;
    } else {
      width = 1030;
      height = 220;
    }

    return { width, height };
  };
  const mapSize = getMapSize(imageArr.length);

  const getListingStatus = () => {
    if (active) {
      return isWaitlisted ? { text: 'Waitlisted', color: 'transparent' } : { text: 'For Rent', color: 'purple' };
    }
    return { text: 'Off Market', color: 'transparent' };
  };

  const tagInfo = getListingStatus();

  const handleOnClick = () => {
    if (onClickEdit) {
      onClickEdit();
    }

    return;
  };

  return (
    <StyledPhotoContainer {...props} className="PhotoGridPDP">
      <StyledOverlay />
      <StyledPhotoGrid className={`grid grid-size-${gridSize}`} data-testid="PhotoGrid">
        {imageArr.map((image: string, idx: number) => {
          const itemClass = `grid-item${idx}`;
          const backgroundImage = { backgroundImage: `url(${image})` };

          return <div data-test="grid-image" key={image} className={itemClass} style={backgroundImage} />;
        })}
        {hasMap && <StaticMap {...mapSize} lat={lat} lng={lng} signUrl={signMapUrl} />}
      </StyledPhotoGrid>
      <StyledPhotoOverlay className="StyledPhotoOverlay">
        <div>
          {/* // eslint-disable-next-line @typescript-eslint/no-explicit-any */}
          <Tag size="sm" appearance={tagInfo.color as any} data-testid="TagInfoText">
            {tagInfo.text}
          </Tag>
          {isFeed && (
            <StyledFeedTag size="sm" appearance="gray" data-testid="FeedTag">
              <b>Read only</b>
            </StyledFeedTag>
          )}
        </div>
        <div>
          {/* // eslint-disable-next-line @typescript-eslint/no-explicit-any */}
          <StyledFlex display="flex" alignItems="baseline" usePointer={onClickEdit as any} onClick={handleOnClick}>
            <StyledAddress fontType="h4" fontColor="white" data-testid="Address">
              {isCommunity && name ? name : [street, unit].join(' ').trim()}
            </StyledAddress>
            {typeof onClickEdit === 'function' && (
              <StyledInlineSpacer marginLeft="xs">
                <IconEdit size="xs" fontColor="white" data-testid="IconEdit" />
              </StyledInlineSpacer>
            )}
          </StyledFlex>
          <Text fontColor="white" as="div" data-testid="CityStateZip">{`${city}, ${state} ${zip}`}</Text>
        </div>
      </StyledPhotoOverlay>
    </StyledPhotoContainer>
  );
};

PhotoGridPDP.propTypes = propTypes;

export default PhotoGridPDP;
